import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { PageHeader, PageSection } from 'common/styles/page';
import { Icon, SemanticCOLORS, SemanticICONS } from 'semantic-ui-react';
import { useGetTeamSnapshotLeadersQuery } from '../../../areas/leaderboard/api/teamLeaderboardApi';
import { CardsGrid, MobilePageContent, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';

const badgeDefinitions: Record<
  string,
  {
    icon: SemanticICONS;
    color: SemanticCOLORS;
    label: string;
    description: string;
    borderColor: string;
    backgroundColor: string;
    metricKey?: string;
    threshold?: number;
  }
> = {
  last_login_in_period: {
    icon: 'sign in alternate',
    color: 'purple',
    label: 'Engaging Leader',
    description: 'Logged in since the last assessment!',
    borderColor: '#8b5cf6',
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    threshold: 1,
  },
  video_watches: {
    icon: 'play circle',
    color: 'blue',
    label: 'Video nuTuber',
    description: 'Watched at least one video resource!',
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
  },
  pdf_opens: {
    icon: 'file pdf',
    color: 'red',
    label: 'Doc Rockstar',
    description: 'Viewed at least one PDF resource!',
    borderColor: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
  },
  file_share_views: {
    icon: 'eye',
    color: 'blue',
    label: 'Share Viewer',
    description: 'A shared resource you sent was viewed by a recipient!',
    borderColor: '#3b82f6',
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
  },
  knowledge_base_shares: {
    icon: 'share',
    color: 'teal',
    label: 'Resource Sharer',
    description: 'Shared a resource from the knowledge base!',
    borderColor: '#14b8a6',
    backgroundColor: 'rgba(20, 184, 166, 0.12)',
  },
  link_opens: {
    icon: 'external alternate',
    color: 'violet',
    label: 'Link Explorer',
    description: 'Viewed at least one link resource!',
    borderColor: '#7c3aed',
    backgroundColor: 'rgba(124, 58, 237, 0.12)',
  },
  focus_plan_interventions: {
    icon: 'clipboard list',
    color: 'green',
    label: 'Focus Planner',
    description: 'Created a focus plan!',
    borderColor: '#22c55e',
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
  },
  action_hero: {
    icon: 'star',
    color: 'orange',
    label: 'Action Hero',
    description: 'Completed at least 5 culture actions!',
    borderColor: '#f59e0b',
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    metricKey: 'focus_plan_culture_actions',
    threshold: 5,
  },
};

const streakBadgeDefinitions: Array<{
  icon: SemanticICONS;
  color: SemanticCOLORS;
  label: string;
  description: string;
  borderColor: string;
  backgroundColor: string;
  threshold: number;
}> = [
  {
    icon: 'trophy',
    color: 'yellow',
    label: 'Streak Champion',
    description: 'Maintained a 20+ week participation streak!',
    borderColor: '#d97706',
    backgroundColor: 'rgba(251, 191, 36, 0.12)',
    threshold: 20,
  },
  {
    icon: 'star',
    color: 'teal',
    label: 'Streak Pro',
    description: 'Maintained a 10+ week participation streak!',
    borderColor: '#0d9488',
    backgroundColor: 'rgba(20, 184, 166, 0.12)',
    threshold: 10,
  },
  {
    icon: 'fire',
    color: 'red',
    label: 'Streaker',
    description: 'Maintained a 5+ week participation streak!',
    borderColor: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
    threshold: 5,
  },
];

const MobileLeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: leaders, isLoading, isFetching, error } = useGetTeamSnapshotLeadersQuery(
    { assessmentPeriodId },
    {
      skip: !assessmentPeriodId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
    }
  );

  const leaderRows = leaders || [];
  const loading = isLoading || isFetching;

  const sortedLeaderRows = useMemo(
    () =>
      [...leaderRows].sort((a, b) => {
        const scoreA = a.score ?? 0;
        const scoreB = b.score ?? 0;
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }

        const nameA = `${a.leader_first_name || ''} ${a.leader_last_name || ''}`.trim();
        const nameB = `${b.leader_first_name || ''} ${b.leader_last_name || ''}`.trim();
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      }),
    [leaderRows]
  );

  const getLeaderIcon = (index: number, score?: number) =>
    index < 3 && score && score > 0 ? 'trophy' : 'user';

  const getLeaderIconColor = (index: number, score?: number) => {
    if (index < 3 && score && score > 0) {
      if (index === 0) return '#fbbf24';
      if (index === 1) return '#6b7280';
      if (index === 2) return '#c2410c';
      return '#60a5fa';
    }

    return score && score > 0 ? '#60a5fa' : '#9ca3af';
  };

  const getLeaderIconBackground = (index: number, score?: number) => {
    if (index < 3 && score && score > 0) {
      if (index === 0) return 'rgba(253, 230, 138, 0.35)';
      if (index === 1) return 'rgba(229, 231, 235, 0.5)';
      if (index === 2) return 'rgba(254, 215, 170, 0.35)';
      return 'transparent';
    }

    return score && score > 0 ? 'rgba(96, 165, 250, 0.12)' : 'rgba(156, 163, 175, 0.12)';
  };

  const formatStreakValue = (leader: any) => {
    const streak = leader.score_components?.participation_streak_weeks ?? 0;
    return streak > 0 ? streak : '-';
  };

  const renderLeaderBadges = (leader: any) => {
    const streak = leader.score_components?.participation_streak_weeks ?? 0;
    const streakBadge = streakBadgeDefinitions.find(badge => streak >= badge.threshold);

    return (
      <BadgeStrip>
        {streakBadge ? (
          <BadgeIcon
            key="streak"
            aria-label={`${streakBadge.label}: ${streakBadge.description}`}
            tabIndex={0}
            color={streakBadge.color}
            borderColor={streakBadge.borderColor}
            backgroundColor={streakBadge.backgroundColor}
          >
            <Icon name={streakBadge.icon} color={streakBadge.color} size="large" />
          </BadgeIcon>
        ) : null}
        {Object.entries(badgeDefinitions).map(([key, badge]) => {
          const metricKey = badge.metricKey || key;
          const count = leader.score_components?.[metricKey] || 0;
          const threshold = badge.threshold ?? 1;
          return count >= threshold ? (
            <BadgeIcon
              key={key}
              aria-label={`${badge.label}: ${badge.description}`}
              tabIndex={0}
              color={badge.color}
              borderColor={badge.borderColor}
              backgroundColor={badge.backgroundColor}
            >
              <Icon name={badge.icon} color={badge.color} size="large" />
            </BadgeIcon>
          ) : null;
        })}
      </BadgeStrip>
    );
  };

  return (
    <PageSection padleft="0" padright="0">
      <PageHeader title="Team Leaderboard" />

      {!assessmentPeriodId ? (
        <MessageContainer>
          <MessageHeading>Please select an assessment period to view team leaders.</MessageHeading>
        </MessageContainer>
      ) : loading ? (
        <MobileSpinner />
      ) : error ? (
        <MessageContainer>
          <MessageHeading>Unable to load leaderboard data.</MessageHeading>
        </MessageContainer>
      ) : (
        <>
          <MobilePageContent>
            <CardsGrid>
            {sortedLeaderRows.length > 0 ? (
              sortedLeaderRows.map((leader, index) => (
                <LeaderCard
                  key={leader.leader_id}
                  type="button"
                  onClick={() => navigate('details', { state: { selectedLeader: leader } })}
                >
                  <LeaderCardHeader>
                    <LeaderAvatar
                      backgroundColor={getLeaderIconBackground(index, leader.score ?? 0)}
                      borderColor={getLeaderIconColor(index, leader.score ?? 0)}
                    >
                      {index < 3 ? (
                        <>
                          <RankOverlay>{index + 1}</RankOverlay>
                          <Icon
                            name="trophy"
                            style={{
                              color: getLeaderIconColor(index, leader.score ?? 0),
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1,
                            }}
                            size="big"
                          />
                        </>
                      ) : (
                        <RankCircle>{index + 1}</RankCircle>
                      )}
                    </LeaderAvatar>
                    <LeaderInfo>
                      <LeaderName>{leader.leader_first_name || ''} {leader.leader_last_name || ''}</LeaderName>
                    </LeaderInfo>
                    <ScorePill>{leader.score ?? 0}</ScorePill>
                  </LeaderCardHeader>

                  <BadgeRow>
                    <BadgeStrip>{renderLeaderBadges(leader)}</BadgeStrip>
                    {(() => {
                      const streakWeeks = leader.score_components?.participation_streak_weeks ?? 0;
                      return streakWeeks > 0 ? (
                        <StreakPill>{streakWeeks} week streak</StreakPill>
                      ) : null;
                    })()}
                  </BadgeRow>
                </LeaderCard>
              ))
            ) : (
              <MobileNoLeaders>
                No team leaders were found for the selected assessment period.
              </MobileNoLeaders>
            )}
            </CardsGrid>
          </MobilePageContent>
        </>
      )}

    </PageSection>
  );
};

export default MobileLeaderboardPage;

const MessageContainer = styled.div`
  padding: 24px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-top: 16px;
`;

const MessageHeading = styled.h2`
  margin: 0;
  font-size: 16px;
  color: #374151;
`;

const BadgeStrip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const BadgeIcon = styled.span<{ color: string; borderColor: string; backgroundColor: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid ${(props) => props.borderColor};
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
  cursor: default;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  i.icon,
  svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
    margin-left: 2px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 0, 0, 0.18);
  }
`;

const LeaderCard = styled.button`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  background: #ffffff;
  padding: 18px;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 1px 6px rgba(15, 23, 42, 0.08);
  transition: transform 150ms ease, box-shadow 150ms ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
    outline: none;
  }
`;

const LeaderCardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const LeaderAvatar = styled.div<{ borderColor: string; backgroundColor: string }>`
  width: 48px;
  height: 48px;
  position: relative;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.backgroundColor};
  border: 1px solid ${(props) => props.borderColor};
  flex-shrink: 0;
`;

const RankOverlay = styled.div`
  position: absolute;
  top: 40%;
  left: 46%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #111827;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
`;
const RankCircle = styled.div`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid rgba(15, 23, 42, 0.12);
`;
const LeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeaderName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScorePill = styled.div`
  min-width: 50px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
  text-align: center;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
`;

const StreakPill = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #0369a1;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const MobileNoLeaders = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #6b7280;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  width: 100%;
  padding: 24px 16px;
`;
